"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Icon from "@/components/ui/Icon";
import { COMPANY_INFO } from "@/data/companyInfo";

interface BlogFloatingCTAProps {
  category?: string;
  articleTitle?: string;
}

export default function BlogFloatingCTA({ category, articleTitle }: BlogFloatingCTAProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show when user has scrolled down into the article (past 500px)
      if (window.scrollY > 500 && !isDismissed) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDismissed]);

  if (isDismissed || !isVisible) return null;

  // Contextual text tailored to category
  const getOfferHeadline = () => {
    if (category?.includes("DevOps") || category?.includes("Cloud")) {
      return "Need enterprise DevOps scaling, Kubernetes or eBPF tuning?";
    }
    if (category?.includes("AI") || category?.includes("LLM")) {
      return "Building custom AI agents or enterprise RAG pipelines?";
    }
    if (category?.includes("Web")) {
      return "Scaling a mission-critical Next.js or React web platform?";
    }
    return "Need senior engineering execution or an architecture audit?";
  };

  return (
    <aside
      aria-label="Direct engineering consultation"
      className="fixed bottom-4 left-4 right-20 sm:left-auto sm:right-24 sm:bottom-6 z-40 max-w-md sm:max-w-lg bg-surface/95 dark:bg-[#1f1a18]/95 backdrop-blur-xl border border-primary/30 p-3.5 sm:p-5 rounded-2xl shadow-2xl transition-all animate-fadeIn"
    >
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-primary font-mono">
            Fast 48-Hour Scoping Available
          </span>
        </div>
        <button
          onClick={() => setIsDismissed(true)}
          className="text-on-surface-variant/60 hover:text-on-surface p-1 rounded-md transition-colors"
          aria-label="Close consultation bar"
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      <p className="text-xs sm:text-sm font-bold text-on-surface leading-snug mb-3.5">
        {getOfferHeadline()}{" "}
        <span className="text-on-surface-variant font-normal block sm:inline">
          Hire our Senior Software Engineers &amp; Cloud Architects.
        </span>
      </p>

      <div className="flex items-center gap-2 sm:gap-2.5">
        <Link
          href="/book-consultation"
          className="flex-1 h-11 flex items-center justify-center text-center bg-primary hover:bg-primary-hover text-on-primary font-bold text-xs sm:text-sm px-3 sm:px-4 rounded-xl transition-all shadow-md hover:scale-[1.02] active:scale-[0.98] leading-snug"
        >
          Book 30-Min Discovery Call
        </Link>
        <a
          href={
            articleTitle
              ? `${COMPANY_INFO.whatsappLink}?text=${encodeURIComponent(
                  `Hi Beyond Ambition team, I just read "${articleTitle}" and would like to discuss a technical consultation.`
                )}`
              : COMPANY_INFO.whatsappLink
          }
          target="_blank"
          rel="noopener noreferrer"
          className="h-11 w-11 shrink-0 flex items-center justify-center bg-[#25D366] hover:bg-[#20ba59] text-white rounded-xl transition-all shadow-md hover:scale-105 active:scale-95"
          aria-label="Chat directly on WhatsApp"
          title="Chat directly on WhatsApp"
        >
          <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.436 2.5 1.171 3.479l-.762 2.784 2.852-.748c.954.582 2.068.919 3.262.919 3.178 0 5.767-2.587 5.768-5.766 0-3.18-2.589-5.766-5.768-5.766zm3.911 8.255c-.174.494-.852.897-1.175.932-.303.033-.699.043-1.144-.099-.291-.093-.664-.224-1.127-.417-1.921-.805-3.14-2.766-3.236-2.893-.095-.128-.777-1.034-.777-1.971 0-.936.48-1.399.664-1.597.16-.171.425-.25.68-.25.084 0 .161.004.23.007.207.01.314.015.452.348.174.417.597 1.458.648 1.563.051.104.084.225.015.362-.069.138-.104.225-.207.348-.104.117-.221.263-.314.348-.104.095-.213.197-.093.407.12.21.534.88 1.146 1.426.787.701 1.448.918 1.652 1.02.203.103.322.086.442-.051.12-.138.514-.597.649-.803.136-.206.273-.172.457-.103.185.069 1.174.553 1.378.656.204.103.339.155.389.24.051.086.051.493-.123.987z"/>
          </svg>
        </a>
      </div>
    </aside>
  );
}
